/**
 * Direct Cognito Authentication Service
 * 
 * This service provides direct AWS Cognito authentication without Amplify.
 * It makes HTTP calls directly to Cognito API endpoints.
 * 
 * Used when VITE_USE_MOCK_AUTH=false in production
 */

const COGNITO_REGION = import.meta.env.VITE_AWS_REGION || 'us-east-1';
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID || '';

// Cognito API endpoint
const getCognitoEndpoint = () => {
  return `https://cognito-idp.${COGNITO_REGION}.amazonaws.com`;
};

interface CognitoTokens {
  AccessToken: string;
  IdToken: string;
  RefreshToken: string;
  ExpiresIn: number;
  TokenType: string;
}

interface CognitoUser {
  Username: string;
  UserAttributes: Array<{ Name: string; Value: string }>;
}

// Store tokens in sessionStorage
const TOKEN_KEY = 'cognito_auth_tokens';
const USER_KEY = 'cognito_auth_user';

// Helper to make Cognito API calls
const cognitoRequest = async (target: string, payload: any): Promise<any> => {
  const endpoint = getCognitoEndpoint();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'X-Amz-Target': target,
      'Content-Type': 'application/x-amz-json-1.1',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorMessage = 'Cognito API error';
    let errorType = '';
    try {
      const error = await response.json();
      errorType = error.__type || '';
      errorMessage = error.message || error.Message || errorType || errorMessage;
      
      // Handle specific error types
      if (errorType === 'NotAuthorizedException') {
        errorMessage = 'Not authorized. Please sign in.';
      } else if (errorType === 'UserNotFoundException') {
        errorMessage = 'User not found.';
      } else if (errorType === 'UsernameExistsException') {
        errorMessage = 'An account with this username or email already exists.';
      } else if (errorType === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements.';
      } else if (errorType === 'InvalidParameterException') {
        errorMessage = error.message || 'Invalid parameters provided.';
      } else if (errorType === 'CodeMismatchException') {
        errorMessage = 'Invalid verification code.';
      } else if (errorType === 'ExpiredCodeException') {
        errorMessage = 'Verification code has expired.';
      } else if (errorType === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later.';
      }
    } catch (e) {
      // If error response is not JSON, use status text
      errorMessage = response.statusText || errorMessage;
    }
    
    const error = new Error(errorMessage);
    (error as any).__type = errorType;
    throw error;
  }

  return response.json();
};

// Import SignInResult from auth.ts to keep types consistent
import type { SignInResult } from './auth';

export const cognitoAuthService = {
  // Sign in
  signIn: async ({ username, password }: { username: string; password: string }): Promise<SignInResult> => {
    try {
      const response = await cognitoRequest('AWSCognitoIdentityProviderService.InitiateAuth', {
        ClientId: CLIENT_ID,
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      });

      // Log the full response for debugging
      console.log('InitiateAuth response:', JSON.stringify(response, null, 2));

      // Check for challenge responses (Cognito returns challenges as successful responses, not errors)
      if (response.ChallengeName) {
        // Handle password change challenge
        if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
          return {
            success: false,
            requiresPasswordChange: true,
            challengeData: response,
            error: 'Password change required',
          };
        }
        
        // Handle MFA challenge - check for MFA-related challenges
        const challengeName = (response.ChallengeName || '').trim();
        console.log('Challenge detected:', challengeName, 'Full response keys:', Object.keys(response));
        
        const isMFAChallenge = challengeName === 'SOFTWARE_TOKEN_MFA' || 
                               challengeName.toUpperCase() === 'SOFTWARE_TOKEN_MFA' ||
                               challengeName.includes('MFA') ||
                               challengeName.includes('SOFTWARE_TOKEN');
        
        if (isMFAChallenge) {
          // Store challenge data for later use (including username from original request)
          const challengeDataWithUsername = {
            ...response,
            Username: username, // Store the username from the sign-in attempt
            ChallengeName: 'SOFTWARE_TOKEN_MFA', // Normalize the challenge name
          };
          sessionStorage.setItem('mfa_challenge_data', JSON.stringify(challengeDataWithUsername));
          
          console.log('MFA challenge detected and returning requiresMFA:', challengeName, 'Session:', response.Session);
          
          return {
            success: false,
            requiresMFA: true,
            challengeData: challengeDataWithUsername,
            error: 'MFA token required',
          };
        }
        
        // Log the actual challenge name for debugging
        console.log('Unhandled challenge:', response.ChallengeName, 'Full response:', response);
        throw new Error(`Challenge required: ${response.ChallengeName}`);
      }

      const tokens: CognitoTokens = response.AuthenticationResult;
      
      // Store tokens
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));

      // Get user info
      await cognitoAuthService.getCurrentUser();

      return { success: true, result: { isSignedIn: true } } as SignInResult;
    } catch (error: any) {
      console.error('Cognito auth sign in error:', error);
      
      // Check if the error message indicates an MFA challenge
      const errorMessage = error.message || '';
      if (errorMessage.includes('SOFTWARE_TOKEN_MFA') || errorMessage.includes('MFA')) {
        // Try to extract challenge data from error if available
        const challengeData = error.response?.data || error.challengeData;
        if (challengeData?.Session) {
          const challengeDataWithUsername = {
            ...challengeData,
            ChallengeName: 'SOFTWARE_TOKEN_MFA',
            Username: username,
          };
          sessionStorage.setItem('mfa_challenge_data', JSON.stringify(challengeDataWithUsername));
          return {
            success: false,
            requiresMFA: true,
            challengeData: challengeDataWithUsername,
            error: 'MFA token required',
          };
        }
      }
      
      return { success: false, error: error.message };
    }
  },

  // Complete new password challenge
  completeNewPassword: async (newPassword: string, session?: string, username?: string) => {
    try {
      if (!session) {
        return { success: false, error: 'Session required for password change' };
      }

      // Get username from stored user if not provided
      if (!username) {
        const userStr = sessionStorage.getItem(USER_KEY);
        if (userStr) {
          const user = JSON.parse(userStr);
          username = user.username;
        }
      }

      const response = await cognitoRequest('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
        ClientId: CLIENT_ID,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
          USERNAME: username || '',
          NEW_PASSWORD: newPassword,
        },
      });

      const tokens: CognitoTokens = response.AuthenticationResult;
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));

      await cognitoAuthService.getCurrentUser();

      return { success: true };
    } catch (error: any) {
      console.error('Cognito complete password error:', error);
      return { success: false, error: error.message };
    }
  },

  // Sign out
  signOut: async () => {
    try {
      const tokensStr = sessionStorage.getItem(TOKEN_KEY);
      if (tokensStr) {
        const tokens: CognitoTokens = JSON.parse(tokensStr);
        
        try {
          await cognitoRequest('AWSCognitoIdentityProviderService.GlobalSignOut', {
            AccessToken: tokens.AccessToken,
          });
        } catch (err) {
          // Continue with local signout even if API call fails
          console.warn('Global sign out failed, clearing local session:', err);
        }
      }

      // Clear local storage
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);

      return { success: true };
    } catch (error: any) {
      // Clear storage even if server call fails
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      return { success: false, error: error.message };
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const tokensStr = sessionStorage.getItem(TOKEN_KEY);
      if (!tokensStr) {
        // No tokens means user is not signed in - this is not an error
        return { success: false, error: 'No authentication tokens found' };
      }

      const tokens: CognitoTokens = JSON.parse(tokensStr);

      const response = await cognitoRequest('AWSCognitoIdentityProviderService.GetUser', {
        AccessToken: tokens.AccessToken,
      });

      const user: CognitoUser = {
        Username: response.Username,
        UserAttributes: response.UserAttributes,
      };

      // Convert attributes array to object
      const attributes: Record<string, any> = {};
      user.UserAttributes.forEach((attr) => {
        attributes[attr.Name] = attr.Value;
      });

      const userData = {
        username: user.Username,
        userId: attributes.sub || user.Username,
        email: attributes.email,
        attributes,
      };

      // Store user info
      sessionStorage.setItem(USER_KEY, JSON.stringify(userData));

      return {
        success: true,
        user: {
          username: userData.username,
          userId: userData.userId,
          signInDetails: {
            loginId: userData.email,
          },
        },
      };
    } catch (error: any) {
      // If it's a NotAuthorizedException, clear tokens and return gracefully
      if (error.__type === 'NotAuthorizedException' || error.message?.includes('Not authorized')) {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(USER_KEY);
        return { success: false, error: 'Not authorized' };
      }
      console.error('Cognito auth get current user error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get current session (includes tokens)
  getCurrentSession: async () => {
    try {
      const tokensStr = sessionStorage.getItem(TOKEN_KEY);
      const userStr = sessionStorage.getItem(USER_KEY);

      if (!tokensStr || !userStr) {
        throw new Error('No session found');
      }

      const tokens: CognitoTokens = JSON.parse(tokensStr);
      const user = JSON.parse(userStr);

      return {
        success: true,
        session: {
          tokens: {
            idToken: {
              toString: () => tokens.IdToken,
              payload: {
                sub: user.userId,
                email: user.email,
                'custom:role': user.attributes['custom:role'] || 'player',
              },
            },
            accessToken: {
              toString: () => tokens.AccessToken,
            },
          },
        },
      };
    } catch (error: any) {
      console.error('Cognito auth get session error:', error);
      return { success: false, error: error.message };
    }
  },

  // Get ID token for API calls
  getIdToken: async (): Promise<string | null> => {
    try {
      const tokensStr = sessionStorage.getItem(TOKEN_KEY);
      if (!tokensStr) {
        return null;
      }

      const tokens: CognitoTokens = JSON.parse(tokensStr);
      return tokens.IdToken;
    } catch (error) {
      console.error('Cognito auth get ID token error:', error);
      return null;
    }
  },

  // Sign up
  signUp: async ({ username, password, email, givenName, familyName, gender, usta_level }: {
    username: string;
    password: string;
    email: string;
    givenName: string;
    familyName: string;
    gender?: string;
    usta_level?: string;
  }) => {
    try {
      // Build user attributes array
      const userAttributes: Array<{ Name: string; Value: string }> = [
        { Name: 'email', Value: email },
        { Name: 'given_name', Value: givenName },
        { Name: 'family_name', Value: familyName },
      ];

      // Set default role to 'player' for new signups
      userAttributes.push({ Name: 'custom:role', Value: 'player' });

      // Add gender if provided
      if (gender && gender.trim() !== '') {
        userAttributes.push({ Name: 'custom:gender', Value: gender });
      }

      // Add USTA level if provided
      if (usta_level && usta_level.trim() !== '') {
        userAttributes.push({ Name: 'custom:usta_level', Value: usta_level });
      }

      const response = await cognitoRequest('AWSCognitoIdentityProviderService.SignUp', {
        ClientId: CLIENT_ID,
        Username: username,
        Password: password,
        UserAttributes: userAttributes,
      });

      // SignUp response includes UserSub and CodeDeliveryDetails if email verification is required
      return {
        success: true,
        result: {
          userSub: response.UserSub,
          codeDeliveryDetails: response.CodeDeliveryDetails,
          userConfirmed: response.UserConfirmed === true,
        },
      };
    } catch (error: any) {
      console.error('Cognito auth sign up error:', error);
      return { success: false, error: error.message };
    }
  },

  // Confirm sign up
  confirmSignUp: async ({ username, code }: { username: string; code: string }) => {
    try {
      await cognitoRequest('AWSCognitoIdentityProviderService.ConfirmSignUp', {
        ClientId: CLIENT_ID,
        Username: username,
        ConfirmationCode: code,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Cognito auth confirm sign up error:', error);
      return { success: false, error: error.message };
    }
  },

  // Resend confirmation code
  resendConfirmationCode: async (username: string) => {
    try {
      const response = await cognitoRequest('AWSCognitoIdentityProviderService.ResendConfirmationCode', {
        ClientId: CLIENT_ID,
        Username: username,
      });

      return {
        success: true,
        result: {
          codeDeliveryDetails: response.CodeDeliveryDetails,
        },
      };
    } catch (error: any) {
      console.error('Cognito auth resend confirmation code error:', error);
      return { success: false, error: error.message };
    }
  },

  // Respond to MFA challenge
  respondToMFAChallenge: async (code: string, session: string, username?: string) => {
    try {
      // Get username from stored challenge data or parameter
      let mfaUsername = username;
      if (!mfaUsername) {
        // Try to get from stored challenge data
        const challengeDataStr = sessionStorage.getItem('mfa_challenge_data');
        if (challengeDataStr) {
          const challengeData = JSON.parse(challengeDataStr);
          mfaUsername = challengeData.ChallengeParameters?.USERNAME || challengeData.Username;
        }
      }

      // For SOFTWARE_TOKEN_MFA, we need USERNAME in ChallengeResponses
      const challengeResponses: Record<string, string> = {
        SOFTWARE_TOKEN_MFA_CODE: code,
      };
      
      // USERNAME is required for SOFTWARE_TOKEN_MFA challenge
      if (mfaUsername) {
        challengeResponses.USERNAME = mfaUsername;
      } else {
        // If we still don't have username, try to get it from the original sign-in attempt
        // This should have been stored when the challenge was received
        throw new Error('Username required for MFA challenge');
      }

      const response = await cognitoRequest('AWSCognitoIdentityProviderService.RespondToAuthChallenge', {
        ClientId: CLIENT_ID,
        ChallengeName: 'SOFTWARE_TOKEN_MFA',
        Session: session,
        ChallengeResponses: challengeResponses,
      });

      if (response.ChallengeName) {
        throw new Error(`Unexpected challenge: ${response.ChallengeName}`);
      }

      const tokens: CognitoTokens = response.AuthenticationResult;
      
      // Store tokens
      sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
      
      // Clear stored challenge data
      sessionStorage.removeItem('mfa_challenge_data');

      // Get user info
      await cognitoAuthService.getCurrentUser();

      return { success: true, result: { isSignedIn: true } } as SignInResult;
    } catch (error: any) {
      console.error('Cognito auth MFA challenge error:', error);
      return { success: false, error: error.message };
    }
  },
};

export default cognitoAuthService;

